import React, { useEffect, useRef, useState } from 'react';
import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    LoadingOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import { Tooltip, Popover, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTaskDrawer } from '../TaskDrawerContext';
import {
    ATTACHMENT_ACCEPT,
    MAX_ATTACHMENT_SIZE_MB,
    formatBytes,
    validateAttachmentFile,
    isImageFile,
    isQuillEmpty,
    setOptimisticImageUrl,
} from '../../utils/attachmentUtils';
import AttachmentItem from './AttachmentItem';
import CommentItem from './CommentItem';
import Avatar from './Avatar';
import { fetchTaskComments } from '../../api/projectManagementApi';
import { fetchFiles, classifyUpload } from '../../files/api/projectFilesApi';
import AiFileOrganizationModal from '../../files/components/AiFileOrganizationModal';
import PreUploadModal from './PreUploadModal';

const commentQuillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean'],
    ],
};

const commentQuillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image',
];

export default function TaskTabs() {
    const {
        task,
        setTask,
        taskId,
        employees,
        currentAdminId,
        activeTab,
        setActiveTab,
        commentText,
        setCommentText,
        submittingComment,
        commentEndRef,
        handleAddComment,
        handleDeleteComment,
        handleDeleteAttachment,
        handleBulkDeleteAttachments,
        // Comment attachments & references
        commentAttachments,
        handleCommentFiles,
        removeCommentAttachment,
        commentFileReferences = [],
        setCommentFileReferences,
        commentMentions = [],
        setCommentMentions,
        // Attachment tab
        pendingAttachments,
        attachmentsDragOver,
        setAttachmentsDragOver,
        handleAttachmentTabFiles,
        handleAttachmentTabDrop,
        handleUploadPendingAttachments,
        removePendingAttachment,
        mode,
    } = useTaskDrawer();

    const [loadingOlderComments, setLoadingOlderComments] = useState(false);
    const [hasMoreOlderComments, setHasMoreOlderComments] = useState(true);

    const handleLoadOlderComments = async () => {
        if (loadingOlderComments || !taskId) return;
        setLoadingOlderComments(true);
        try {
            const currentCount = (task?.comments || []).length;
            const { data } = await fetchTaskComments(taskId, { skip: currentCount, limit: 10 });
            if (data?.success && Array.isArray(data.result)) {
                const incoming = data.result;
                if (incoming.length === 0 || data.hasMore === false) {
                    setHasMoreOlderComments(false);
                }
                if (incoming.length > 0 && setTask) {
                    setTask((prev) => {
                        const existingIds = new Set((prev?.comments || []).map((c) => String(c._id)));
                        const uniqueIncoming = incoming.filter((c) => !existingIds.has(String(c._id)));
                        return {
                            ...prev,
                            comments: [...(prev?.comments || []), ...uniqueIncoming],
                        };
                    });
                }
            } else {
                setHasMoreOlderComments(false);
            }
        } catch {
            // graceful fallback
        } finally {
            setLoadingOlderComments(false);
        }
    };

    // Multi-select & Bulk Delete for Attachments
    const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Keep selectedAttachmentIds in sync if attachments change or are deleted
    useEffect(() => {
        if (!task?.attachments?.length) {
            setSelectedAttachmentIds([]);
        } else {
            const validIds = new Set(task.attachments.map((a) => String(a._id)));
            setSelectedAttachmentIds((prev) => prev.map(String).filter((id) => validIds.has(id)));
        }
    }, [task?.attachments]);

    const handleToggleSelectAttachment = (attachmentId) => {
        const idStr = String(attachmentId);
        setSelectedAttachmentIds((prev) =>
            prev.map(String).includes(idStr)
                ? prev.filter((id) => String(id) !== idStr)
                : [...prev, idStr]
        );
    };

    const handleToggleSelectAllAttachments = () => {
        const allAttachments = task?.attachments || [];
        if (selectedAttachmentIds.length === allAttachments.length) {
            setSelectedAttachmentIds([]);
        } else {
            setSelectedAttachmentIds(allAttachments.map((a) => String(a._id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!selectedAttachmentIds.length || isBulkDeleting || !handleBulkDeleteAttachments) return;
        setIsBulkDeleting(true);
        try {
            const deletedIds = await handleBulkDeleteAttachments(selectedAttachmentIds);
            if (deletedIds?.length) {
                setSelectedAttachmentIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
            }
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // @File Project Reference Picker state
    const [projectFiles, setProjectFiles] = useState([]);
    const [loadingProjectFiles, setLoadingProjectFiles] = useState(false);
    const [filePickerOpen, setFilePickerOpen] = useState(false);

    const targetProjectId = task?.project?._id || (typeof task?.project === 'string' ? task?.project : null);

    const loadProjectFiles = async () => {
        if (!targetProjectId && (!task?.attachments || task.attachments.length === 0)) return;
        setLoadingProjectFiles(true);
        try {
            let combined = [];
            if (targetProjectId) {
                const res = await fetchFiles({ projectId: targetProjectId, status: 'active' });
                if (res?.success && Array.isArray(res.data)) {
                    combined = [...res.data];
                }
            }
            // Merge task attachments so task deliverables & comment files are immediately referenceable
            if (Array.isArray(task?.attachments)) {
                task.attachments.forEach((att) => {
                    const attId = String(att._id || att.id);
                    if (!combined.some((f) => String(f.id || f._id) === attId)) {
                        combined.push({
                            id: attId,
                            _id: attId,
                            name: att.originalName || att.fileName || 'Attachment',
                            size: formatBytes(att.size || 0),
                            folderCode: 'Task Attachment',
                            publicUrl: att.publicUrl,
                            icon: 'attach_file',
                        });
                    }
                });
            }
            setProjectFiles(combined);
        } catch (e) {
            console.error('Error fetching project files for reference:', e);
        } finally {
            setLoadingProjectFiles(false);
        }
    };

    // Preload project files when task loads or when comments tab is active
    useEffect(() => {
        if (targetProjectId || (task?.attachments && task.attachments.length > 0)) {
            loadProjectFiles();
        }
    }, [targetProjectId, task?._id, task?.attachments?.length]);

    // AI File Organization State (Section 21)
    const [aiOrgModalOpen, setAiOrgModalOpen] = useState(false);
    const [aiOrgFile, setAiOrgFile] = useState(null);
    const [aiOrgData, setAiOrgData] = useState(null);
    const [aiOrgTargetContext, setAiOrgTargetContext] = useState('comment');

    // Pre-Upload Modal state — collects one-word description + reviewer tags before AI classification
    const [preUploadOpen, setPreUploadOpen] = useState(false);
    const [preUploadFile, setPreUploadFile] = useState(null);
    const [preUploadContext, setPreUploadContext] = useState('comment');
    const [uploadSizeError, setUploadSizeError] = useState('');

    /**
     * Entry point for every file selection (click or drag-drop).
     * Step 1: Validate size & type.
     * Step 2: Open PreUploadModal to collect description + reviewer tags.
     * Step 3: After confirmation, run AI classifyUpload → AI Org Modal.
     */
    const handleInitiateUpload = (fileList, context = 'comment') => {
        if (!fileList || fileList.length === 0) return;
        if (preUploadOpen || aiOrgModalOpen) {
            message.warning('Please finish configuring the current file first.');
            return;
        }
        const rawFiles = Array.from(fileList);
        if (rawFiles.length > 1) {
            message.warning('Please attach, paste, or drop one file at a time.');
        }
        const primaryFile = rawFiles[0];

        // ── Client-side size & type validation (100 MB = 104,857,600 bytes) ──
        const validation = validateAttachmentFile(primaryFile);
        if (!validation.valid) {
            setUploadSizeError(validation.reason);
            // Auto-clear the error after 5 s
            setTimeout(() => setUploadSizeError(''), 5000);
            return;
        }
        setUploadSizeError('');

        if (!targetProjectId) {
            // No project context — skip AI flow, attach directly (single file)
            if (context === 'comment') handleCommentFiles([primaryFile]);
            else handleAttachmentTabFiles([primaryFile]);
            return;
        }

        // Open pre-upload description + reviewer modal
        setPreUploadFile(primaryFile);
        setPreUploadContext(context);
        setPreUploadOpen(true);
    };

    /**
     * Called when user confirms the PreUploadModal (description + reviewers set).
     * Runs AI classify-upload then opens AI File Organization Modal.
     */
    const handlePreUploadConfirm = async ({ description, taggedReviewers }) => {
        setPreUploadOpen(false);
        const file = preUploadFile;
        const context = preUploadContext;
        if (!file) return;

        try {
            const res = await classifyUpload({
                projectId: targetProjectId,
                taskId: task?._id,
                fileName: file.name,
                mimeType: file.type,
                // Inject the description label into commentText so classifyUpload uses it for context
                commentText: `${description} ${commentText || ''}`.trim(),
            });

            if (res?.success && res.data) {
                // Attach metadata for downstream handlers
                file._preUploadMeta = { description, taggedReviewers };
                setAiOrgFile(file);
                setAiOrgData(res.data);
                setAiOrgTargetContext(context);
                setAiOrgModalOpen(true);
            } else {
                if (context === 'comment') handleCommentFiles([file]);
                else handleAttachmentTabFiles([file]);
            }
        } catch (e) {
            console.error('Error in AI file classification:', e);
            if (context === 'comment') handleCommentFiles([file]);
            else handleAttachmentTabFiles([file]);
        }
    };

    const handleAcceptAiOrganization = (configuredData) => {
        if (!aiOrgFile) return;
        // Carry pre-upload metadata (description + reviewer tags) into the AI config
        const meta = aiOrgFile._preUploadMeta || {};
        aiOrgFile.aiConfig = { ...configuredData, ...meta };
        if (configuredData.customFileName) {
            try {
                Object.defineProperty(aiOrgFile, 'name', {
                    writable: true,
                    value: configuredData.customFileName,
                });
            } catch {
                // Ignore if read-only in some browser engines
            }
        }

        if (aiOrgTargetContext === 'comment') {
            handleCommentFiles([aiOrgFile]);
        } else {
            handleAttachmentTabFiles([aiOrgFile]);
        }
        setAiOrgModalOpen(false);
        setAiOrgFile(null);
        setAiOrgData(null);
    };

    const handleChooseExistingFileFromAi = () => {
        setAiOrgModalOpen(false);
        setAiOrgFile(null);
        setAiOrgData(null);
        loadProjectFiles();
        setFilePickerOpen(true);
    };

    // Separate file input refs so they don't conflict
    const attachTabInputRef = useRef(null);
    const commentInputRef = useRef(null);

    const hasPendingToUpload = pendingAttachments.some((f) => f.status === 'pending' || f.status === 'error');

    // Mention & Autocomplete state
    const [mentionState, setMentionState] = useState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const quillCommentRef = useRef(null);
    const mentionListRef = useRef(null); // for auto-scroll on arrow key navigation
    const lastCommentSelectionRangeRef = useRef(null);
    const postPasteCommentCursorPosRef = useRef(null);
    const handleCommentPasteRef = useRef(null);

    // Restore cursor position in comment editor after ReactQuill's controlled value re-render
    useEffect(() => {
        if (postPasteCommentCursorPosRef.current !== null) {
            const targetPos = postPasteCommentCursorPosRef.current;
            postPasteCommentCursorPosRef.current = null;
            setTimeout(() => {
                const editor = quillCommentRef.current?.getEditor?.();
                if (editor) {
                    try {
                        editor.focus();
                        editor.setSelection(targetPos, 0, 'user');
                        if (quillCommentRef.current?.setEditorSelection) {
                            quillCommentRef.current.setEditorSelection(editor, { index: targetPos, length: 0 });
                        }
                    } catch { /* ignore */ }
                }
            }, 10);
        }
    }, [commentText]);

    // Attach native capturing paste listener directly to Comment Quill's editor root
    useEffect(() => {
        let editorRoot = null;
        const nativePasteHandler = (e) => {
            handleCommentPasteRef.current?.(e);
        };
        const timer = setTimeout(() => {
            const editor = quillCommentRef.current?.getEditor?.();
            if (editor?.root) {
                editorRoot = editor.root;
                editorRoot.addEventListener('paste', nativePasteHandler, true);
            }
        }, 100);
        return () => {
            clearTimeout(timer);
            if (editorRoot) {
                editorRoot.removeEventListener('paste', nativePasteHandler, true);
            }
        };
    }, []);

    // Auto-scroll the highlighted mention item into view when navigating with arrow keys
    useEffect(() => {
        if (!mentionState.active || !mentionListRef.current) return;
        const el = mentionListRef.current.querySelector('[data-mention-selected="true"]');
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [selectedMentionIndex, mentionState.active]);

    const filteredPeople = (employees || []).filter((emp) => {
        if (!mentionState.query) return true;
        const q = mentionState.query.toLowerCase();
        const name = (emp.name || '').toLowerCase();
        const surname = (emp.surname || '').toLowerCase();
        const email = (emp.email || '').toLowerCase();
        return name.includes(q) || surname.includes(q) || `${name} ${surname}`.includes(q) || email.includes(q);
    }).slice(0, 20);

    const filteredProjectFiles = projectFiles.filter((file) => {
        if (!mentionState.query) return true;
        const q = mentionState.query.toLowerCase();
        return (
            (file.name || '').toLowerCase().includes(q) ||
            (file.folderCode || '').toLowerCase().includes(q) ||
            (file.revisionRound || '').toLowerCase().includes(q)
        );
    }).slice(0, 15);

    const totalMentionItems = [
        ...filteredPeople.map((p) => ({ type: 'person', data: p })),
        ...filteredProjectFiles.map((f) => ({ type: 'file', data: f })),
    ];

    const handleSelectUserMention = (user) => {
        const username = user.name || user.fullName || user.email?.split('@')[0] || 'user';
        const targetUserId = user.id || user._id;
        if (targetUserId && setCommentMentions) {
            setCommentMentions((prev) => {
                const current = Array.isArray(prev) ? prev : [];
                if (current.some((m) => String(m.userId) === String(targetUserId))) return current;
                return [
                    ...current,
                    {
                        userId: targetUserId,
                        name: user.fullName || user.name || username,
                        email: user.email || '',
                    },
                ];
            });
        }
        if (quillCommentRef.current) {
            const editor = quillCommentRef.current.getEditor();
            if (mentionState.active) {
                const matchStart = Math.max(0, mentionState.matchStart);
                const deleteLen = Math.max(0, mentionState.cursorPosition - matchStart);
                editor.deleteText(matchStart, deleteLen);
                editor.insertText(matchStart, `@${username} `);
                editor.setSelection(matchStart + username.length + 2);
                setCommentText(editor.root.innerHTML);
            } else {
                const range = editor.getSelection();
                const idx = range ? range.index : Math.max(0, editor.getLength() - 1);
                editor.insertText(idx, `@${username} `);
                editor.setSelection(idx + username.length + 2);
                setCommentText(editor.root.innerHTML);
            }
            editor.focus();
        } else {
            setCommentText((prev) => (prev ? `${prev} @${username} ` : `@${username} `));
        }
        setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
    };

    const handleSelectFileReference = (file) => {
        const fileId = String(file.id || file._id);
        if (!commentFileReferences.some((f) => String(f.id || f._id) === fileId)) {
            setCommentFileReferences((prev) => [...prev, file]);
        }
        if (quillCommentRef.current) {
            const editor = quillCommentRef.current.getEditor();
            if (mentionState.active) {
                const matchStart = Math.max(0, mentionState.matchStart);
                const deleteLen = Math.max(0, mentionState.cursorPosition - matchStart);
                editor.deleteText(matchStart, deleteLen);
                editor.insertText(matchStart, `@File[${file.name}] `);
                editor.setSelection(matchStart + file.name.length + 8);
                setCommentText(editor.root.innerHTML);
            } else {
                const range = editor.getSelection();
                const idx = range ? range.index : Math.max(0, editor.getLength() - 1);
                editor.insertText(idx, `@File[${file.name}] `);
                editor.setSelection(idx + file.name.length + 8);
                setCommentText(editor.root.innerHTML);
            }
            editor.focus();
        } else {
            setCommentText((prev) => (prev ? `${prev} @File[${file.name}] ` : `@File[${file.name}] `));
        }
        setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
        setFilePickerOpen(false);
    };

    const handleCommentQuillChange = (content, delta, source, editor) => {
        setCommentText(content);
        if (source === 'user') {
            const range = editor.getSelection();
            if (range) {
                const textBeforeCursor = editor.getText(0, range.index);
                const match = textBeforeCursor.match(/(?:^|\s)@([^\s]*)$/);
                if (match) {
                    const query = match[1];
                    const matchStart = range.index - query.length - 1;
                    setMentionState({ active: true, query, matchStart, cursorPosition: range.index });
                    setSelectedMentionIndex(0);
                    if (projectFiles.length === 0) {
                        loadProjectFiles();
                    }
                } else if (mentionState.active) {
                    setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
                }
            }
        }
    };

    const handleCommentSelectionChange = (range, source, editor) => {
        if (range) {
            lastCommentSelectionRangeRef.current = range;
        }
        if (source === 'user' && range) {
            const textBeforeCursor = editor.getText(0, range.index);
            const match = textBeforeCursor.match(/(?:^|\s)@([^\s]*)$/);
            if (match) {
                const query = match[1];
                const matchStart = range.index - query.length - 1;
                setMentionState({ active: true, query, matchStart, cursorPosition: range.index });
                setSelectedMentionIndex(0);
                if (projectFiles.length === 0) {
                    loadProjectFiles();
                }
            } else if (mentionState.active) {
                setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
            }
        }
    };

    const handleCommentQuillKeyDown = (e) => {
        if (mentionState.active && totalMentionItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex((prev) => (prev + 1) % totalMentionItems.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex((prev) => (prev - 1 + totalMentionItems.length) % totalMentionItems.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const target = totalMentionItems[selectedMentionIndex] || totalMentionItems[0];
                if (target) {
                    if (target.type === 'person') handleSelectUserMention(target.data);
                    else if (target.type === 'file') handleSelectFileReference(target.data);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionState({ active: false, query: '', matchStart: 0, cursorPosition: 0 });
                return;
            }
        }

        // Shortcut to submit comment: Ctrl+Enter or Cmd+Enter
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleAddComment();
        }
    };

    const lastPasteTimeRef = useRef(0);
    const [isCommentDragOver, setIsCommentDragOver] = useState(false);

    const getCommentPasteIndex = () => {
        const editor = quillCommentRef.current?.getEditor?.();
        if (!editor) return 0;
        try {
            const domSelection = window.getSelection();
            if (domSelection && domSelection.rangeCount > 0 && editor.root.contains(domSelection.anchorNode)) {
                const range = domSelection.getRangeAt(0);
                const quillRange = editor.constructor.find(domSelection.anchorNode, true);
                if (quillRange) {
                    const domIndex = editor.getIndex(quillRange);
                    const offset = range.startOffset || 0;
                    if (typeof domIndex === 'number' && !isNaN(domIndex)) {
                        return Math.max(0, Math.min(domIndex + offset, editor.getLength() - 1));
                    }
                }
            }
        } catch { /* fallback */ }
        const quillSel = editor.getSelection();
        if (quillSel && typeof quillSel.index === 'number') return quillSel.index;
        if (lastCommentSelectionRangeRef.current && typeof lastCommentSelectionRangeRef.current.index === 'number') {
            return lastCommentSelectionRangeRef.current.index;
        }
        return Math.max(0, editor.getLength() - 1);
    };

    const handleCommentPaste = (e) => {
        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        const now = Date.now();
        if (now - lastPasteTimeRef.current < 400) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        const items = clipboardData.items;
        const files = clipboardData.files;
        const pastedFiles = [];

        // 1. Check DataTransferItemList (standard for screenshots / copied image data)
        if (items && items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type && item.type.startsWith('image/')) {
                    const blob = item.getAsFile();
                    if (blob) {
                        const ext = blob.type ? blob.type.split('/')[1] || 'png' : 'png';
                        const cleanExt = ext === 'jpeg' ? 'jpg' : ext.replace('+xml', '');
                        const timestamp = dayjs().format('YYYYMMDD-HHmmss');
                        const fileName = (blob.name && blob.name !== 'image.png')
                            ? blob.name
                            : `pasted-image-${timestamp}-${i + 1}.${cleanExt}`;
                        const file = new File([blob], fileName, { type: blob.type || 'image/png' });
                        pastedFiles.push(file);
                    }
                }
            }
        }

        // 2. Fallback to clipboardData.files (any files copied from desktop/explorer)
        if (pastedFiles.length === 0 && files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                pastedFiles.push(files[i]);
            }
        }

        if (pastedFiles.length > 0) {
            lastPasteTimeRef.current = now;
            e.preventDefault();
            e.stopPropagation();

            handleInitiateUpload(pastedFiles, 'comment');
            return;
        }

        // 3. Auto-linkify pasted URLs in the comment rich text editor
        const pastedText = clipboardData.getData('text/plain')?.trim();
        if (pastedText && /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i.test(pastedText) && quillCommentRef.current) {
            lastPasteTimeRef.current = now;
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();

            const editor = quillCommentRef.current.getEditor();
            const insertIndex = getCommentPasteIndex();
            const href = pastedText.startsWith('http') ? pastedText : `https://${pastedText}`;

            let deleteLen = 0;
            try {
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0 && editor.root.contains(sel.anchorNode)) {
                    deleteLen = sel.getRangeAt(0).toString().length;
                }
            } catch { /* ignore */ }
            if (deleteLen > 0) {
                editor.deleteText(insertIndex, deleteLen, 'user');
            }

            // Insert link and trailing space so caret cleanly moves outside <a>
            editor.insertText(insertIndex, pastedText, 'link', href, 'user');
            editor.insertText(insertIndex + pastedText.length, ' ', 'user');

            const newCursorPos = insertIndex + pastedText.length + 1;
            lastCommentSelectionRangeRef.current = { index: newCursorPos, length: 0 };
            postPasteCommentCursorPosRef.current = newCursorPos;

            editor.focus();
            editor.setSelection(newCursorPos, 0, 'user');
            if (quillCommentRef.current?.setEditorSelection) {
                try {
                    quillCommentRef.current.setEditorSelection(editor, { index: newCursorPos, length: 0 });
                } catch { /* ignore */ }
            }

            setCommentText(editor.root.innerHTML);

            setTimeout(() => {
                try {
                    editor.focus();
                    editor.setSelection(newCursorPos, 0, 'user');
                    if (quillCommentRef.current?.setEditorSelection) {
                        quillCommentRef.current.setEditorSelection(editor, { index: newCursorPos, length: 0 });
                    }
                } catch { /* ignore */ }
            }, 20);
            setTimeout(() => {
                try {
                    editor.focus();
                    editor.setSelection(newCursorPos, 0, 'user');
                    if (quillCommentRef.current?.setEditorSelection) {
                        quillCommentRef.current.setEditorSelection(editor, { index: newCursorPos, length: 0 });
                    }
                } catch { /* ignore */ }
            }, 80);
            return;
        }
    };

    handleCommentPasteRef.current = handleCommentPaste;

    const handleCommentDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isCommentDragOver) setIsCommentDragOver(true);
    };

    const handleCommentDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCommentDragOver(false);
    };

    const handleCommentDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCommentDragOver(false);
        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;

        handleInitiateUpload(files, 'comment');
    };

    return (
        <>
            <section data-tour="task-tabs-section" className="space-y-4">
                <style>{`
                    .comment-quill-wrapper .ql-toolbar.ql-snow {
                        border: none !important;
                        border-bottom: 1px solid #e2e8f0 !important;
                        background: #f8fafc;
                        padding: 4px 8px;
                    }
                    .comment-quill-wrapper .ql-toolbar.ql-snow .ql-formats {
                        margin-right: 6px;
                    }
                    .comment-quill-wrapper .ql-container.ql-snow {
                        border: none !important;
                        font-family: inherit;
                        font-size: 13px;
                        background: #ffffff;
                    }
                    .comment-quill-wrapper .ql-editor {
                        min-height: 48px;
                        max-height: 160px;
                        overflow-y: auto;
                        font-size: 13px;
                        line-height: 1.4;
                        color: #1e293b;
                        padding: 6px 10px;
                    }
                    .comment-quill-wrapper .ql-editor p {
                        margin-bottom: 0.25rem;
                    }
                    .comment-quill-wrapper .ql-editor p:last-child {
                        margin-bottom: 0;
                    }
                    .comment-quill-wrapper .ql-editor ul,
                    .comment-quill-wrapper .ql-editor ol {
                        padding-left: 1.25rem;
                        margin-bottom: 0.25rem;
                    }
                    .comment-quill-wrapper .ql-editor blockquote {
                        border-left: 3px solid #03488a;
                        padding-left: 0.75rem;
                        color: #64748b;
                        margin: 0.25rem 0;
                    }
                    .comment-quill-wrapper .ql-editor pre.ql-syntax {
                        background-color: #1e293b;
                        color: #f8fafc;
                        border-radius: 0.5rem;
                        padding: 0.5rem 0.75rem;
                        font-size: 12px;
                        margin: 0.25rem 0;
                    }
                    .comment-quill-wrapper .ql-editor.ql-blank::before {
                        color: #94a3b8;
                        font-style: italic;
                        font-size: 13px;
                        left: 12px;
                    }
                    .dark .comment-quill-wrapper .ql-toolbar.ql-snow {
                        background: #182234 !important;
                        border-bottom-color: #334155 !important;
                    }
                    .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-stroke {
                        stroke: #94a3b8 !important;
                    }
                    .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-fill {
                        fill: #94a3b8 !important;
                    }
                    .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-picker {
                        color: #94a3b8 !important;
                    }
                    .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-picker-options {
                        background-color: #1e293b !important;
                        border-color: #334155 !important;
                        color: #e2e8f0 !important;
                    }
                    .dark .comment-quill-wrapper .ql-container.ql-snow {
                        background: #141414 !important;
                    }
                    .dark .comment-quill-wrapper .ql-editor {
                        color: #f1f5f9;
                    }
                    .dark .comment-quill-wrapper .ql-editor blockquote {
                        border-left-color: #38bdf8;
                        color: #94a3b8;
                    }
                    .dark .comment-quill-wrapper .ql-editor.ql-blank::before {
                        color: #64748b;
                    }
                    .comment-quill-wrapper .ql-editor a {
                        color: #0070F3 !important;
                        text-decoration: underline !important;
                        word-break: break-all;
                        cursor: pointer;
                    }
                    .comment-quill-wrapper .ql-editor a:hover {
                        color: #0051b3 !important;
                    }
                    .dark .comment-quill-wrapper .ql-editor a {
                        color: #38bdf8 !important;
                    }
                    .dark .comment-quill-wrapper .ql-editor a:hover {
                        color: #7dd3fc !important;
                    }
                `}</style>
                {mode === 'create' ? (
                    <>
                        <div className="flex items-center border-b border-surface-container-high gap-1 opacity-50 pointer-events-none">
                            <TabButton id="comments" activeTab="comments">Comments (0)</TabButton>
                            <TabButton id="attachments" activeTab="">Attachments (0)</TabButton>
                        </div>
                        <div className="flex flex-col items-center justify-center py-8 text-center bg-surface-container-lowest border border-dashed border-surface-container-high rounded-xl">
                            <span className="material-symbols-outlined text-3xl text-on-surface-variant opacity-50 mb-2">lock</span>
                            <p className="text-sm font-semibold text-on-surface-variant">Save task to unlock tabs</p>
                            <p className="text-xs text-on-surface-variant opacity-70 mt-1">Comments and attachments will be available after creation.</p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Tab bar */}
                        <div data-tour="task-tabs-nav" className="flex items-center border-b border-surface-container-high dark:border-slate-800 gap-1">
                            <TabButton id="comments" activeTab={activeTab} setActiveTab={setActiveTab}>
                                Comments ({task.comments?.length || 0})
                            </TabButton>
                            <TabButton id="attachments" activeTab={activeTab} setActiveTab={setActiveTab}>
                                Attachments ({task.attachments?.length || 0})
                            </TabButton>
                        </div>

                        {/* ── Comments Tab ────────────────────────────────────────── */}
                        {activeTab === 'comments' && (() => {
                            const rawComments = task.comments || [];
                            // Sort chronologically: OLDEST first, NEWEST at bottom
                            const commentsToDisplay = [...rawComments].sort(
                                (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
                            );
                            const canLoadMore = hasMoreOlderComments && rawComments.length >= 10;

                            return (
                                <div className="space-y-4">
                                    {/* Load older comments — at the TOP */}
                                    {canLoadMore && (
                                        <div className="text-center pt-1 pb-2">
                                            <button
                                                onClick={handleLoadOlderComments}
                                                disabled={loadingOlderComments}
                                                className="text-xs font-bold text-[#03488a] dark:text-blue-400 hover:underline px-4 py-1.5 bg-blue-50 dark:bg-blue-950/40 rounded-full transition-colors cursor-pointer border border-blue-200 dark:border-blue-800/60 inline-flex items-center gap-1.5 disabled:opacity-50"
                                            >
                                                {loadingOlderComments ? (
                                                    <>
                                                        <LoadingOutlined style={{ fontSize: 13 }} />
                                                        <span>Loading earlier comments…</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px]">history</span>
                                                        <span>Load earlier comments</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {/* Comment list — oldest at top, newest at bottom */}
                                    <div className="space-y-5 pr-1">
                                        {commentsToDisplay.map((comment) => (
                                            <CommentItem
                                                key={comment._id}
                                                comment={comment}
                                                currentAdminId={currentAdminId}
                                                onDelete={handleDeleteComment}
                                                taskId={taskId}
                                            />
                                        ))}

                                        {(!rawComments || rawComments.length === 0) && (
                                            <p className="text-center text-xs text-ed-on-surface-variant opacity-60 py-4">
                                                No comments yet. Start the conversation!
                                            </p>
                                        )}
                                        <div ref={commentEndRef} />
                                    </div>


                                    <div
                                        onDragOver={handleCommentDragOver}
                                        onDragLeave={handleCommentDragLeave}
                                        onDrop={handleCommentDrop}
                                        className={`space-y-2 border-t border-slate-200 dark:border-slate-800 pt-3 relative transition-all ${isCommentDragOver ? 'ring-2 ring-[#03488a] rounded-xl bg-blue-50/50 dark:bg-blue-950/30 p-2' : ''
                                            }`}
                                    >
                                        {isCommentDragOver && (
                                            <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-50/90 dark:bg-slate-900/90 rounded-xl border-2 border-dashed border-[#03488a] pointer-events-none">
                                                <div className="flex items-center gap-2 text-xs font-bold text-[#03488a] dark:text-blue-400">
                                                    <span className="material-symbols-outlined text-lg">cloud_upload</span>
                                                    Drop a file here to attach to comment
                                                </div>
                                            </div>
                                        )}
                                        {/* Text input row with avatar on left and inline actions */}
                                        <div className="flex gap-3 items-start">
                                            <Avatar person={(employees || []).find(e => e._id === currentAdminId)} size={32} />
                                            <div className="flex-1 relative">
                                                {/* Inline @ Mention Popup */}
                                                {mentionState.active && (
                                                    <div className="absolute bottom-full left-0 mb-2 w-80 max-h-80 bg-white dark:bg-[#1e1e1e] rounded-xl shadow-xl border border-slate-200 dark:border-[#333] z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-150">
                                                        <div className="px-3 py-1.5 bg-slate-50 dark:bg-[#252525] border-b border-slate-100 dark:border-[#333] flex items-center justify-between text-[11px] font-bold text-slate-500">
                                                            <span className="flex items-center gap-1.5 text-[#03488A] dark:text-[#00AAF1]">
                                                                <span className="material-symbols-outlined text-[15px]">alternate_email</span>
                                                                MENTION USER OR FILE
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 font-normal">Use ↑↓ & Enter</span>
                                                        </div>
                                                        <div ref={mentionListRef} className="overflow-y-auto max-h-[260px] p-1 space-y-1">
                                                            {/* Section 1: People */}
                                                            {filteredPeople.length > 0 && (
                                                                <div>
                                                                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                                        People
                                                                    </div>
                                                                    {filteredPeople.map((person, idx) => (
                                                                        <div
                                                                            key={person._id || idx}
                                                                            data-mention-selected={idx === selectedMentionIndex ? 'true' : 'false'}
                                                                            onMouseEnter={() => setSelectedMentionIndex(idx)}
                                                                            onClick={() => handleSelectUserMention(person)}
                                                                            className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${idx === selectedMentionIndex
                                                                                ? 'bg-[#eff4ff] text-[#03488a] dark:bg-[#1a2538] dark:text-white'
                                                                                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                }`}
                                                                        >
                                                                            <Avatar person={person} size={22} />
                                                                            <div className="truncate flex-1 min-w-0">
                                                                                <p className="font-semibold truncate text-[12px]">
                                                                                    {person.name} {person.surname}
                                                                                </p>
                                                                                <p className="text-[10px] text-slate-400 truncate">
                                                                                    @{person.name || person.email?.split('@')[0]} {person.department ? `· ${person.department}` : ''}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Section 2: Project Files */}
                                                            {filteredProjectFiles.length > 0 && (
                                                                <div>
                                                                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-t border-slate-100 dark:border-[#333] mt-1 pt-1">
                                                                        Project Files (@File)
                                                                    </div>
                                                                    {filteredProjectFiles.map((file, idx) => {
                                                                        const actualIdx = filteredPeople.length + idx;
                                                                        return (
                                                                            <div
                                                                                key={file.id || file._id}
                                                                                data-mention-selected={actualIdx === selectedMentionIndex ? 'true' : 'false'}
                                                                                onMouseEnter={() => setSelectedMentionIndex(actualIdx)}
                                                                                onClick={() => handleSelectFileReference(file)}
                                                                                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${actualIdx === selectedMentionIndex
                                                                                    ? 'bg-[#eff4ff] text-[#0070F3] dark:bg-[#1a2538] dark:text-white'
                                                                                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                    }`}
                                                                            >
                                                                                <span className="material-symbols-outlined text-[17px] text-[#0070F3]">
                                                                                    {file.icon || 'description'}
                                                                                </span>
                                                                                <div className="truncate flex-1 min-w-0">
                                                                                    <p className="font-semibold truncate text-[12px]">{file.name}</p>
                                                                                    <p className="text-[10px] text-slate-400 truncate">
                                                                                        {file.folderCode ? `Folder ${file.folderCode}` : ''} {file.size ? `· ${file.size}` : ''}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}

                                                            {totalMentionItems.length === 0 && (
                                                                <div className="py-3 text-center text-xs text-slate-400">
                                                                    No matching people or project files
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                <div
                                                    onPaste={handleCommentPaste}
                                                    onClick={(e) => {
                                                        if (e.target.tagName === 'A' || e.target.closest('a')) {
                                                            const anchor = e.target.tagName === 'A' ? e.target : e.target.closest('a');
                                                            const href = anchor?.getAttribute('href');
                                                            if (href && (e.ctrlKey || e.metaKey)) {
                                                                e.preventDefault();
                                                                window.open(href, '_blank', 'noopener,noreferrer');
                                                            }
                                                        }
                                                    }}
                                                    className="comment-quill-wrapper rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#141414] shadow-2xs overflow-hidden transition-all focus-within:ring-1 focus-within:ring-primary focus-within:border-primary"
                                                >
                                                    <ReactQuill
                                                        ref={quillCommentRef}
                                                        theme="snow"
                                                        value={commentText || ''}
                                                        onChange={handleCommentQuillChange}
                                                        onChangeSelection={handleCommentSelectionChange}
                                                        onKeyDown={handleCommentQuillKeyDown}
                                                        modules={commentQuillModules}
                                                        formats={commentQuillFormats}
                                                        placeholder="Add a comment… (Paste images with Ctrl+V, or type @ to mention people or files)"
                                                    />

                                                    {/* Pending comment attachments & @File references docked inside editor box */}
                                                    {(commentAttachments.length > 0 || (commentFileReferences && commentFileReferences.length > 0)) && (
                                                        <div className="flex flex-wrap gap-2 px-3 py-2 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-[#1a2234]/40 max-h-36 overflow-y-auto">
                                                            {commentAttachments.map((entry) => (
                                                                <PendingFileChip
                                                                    key={entry.id}
                                                                    entry={entry}
                                                                    onRemove={removeCommentAttachment}
                                                                />
                                                            ))}
                                                            {(commentFileReferences || []).map((ref) => (
                                                                <span
                                                                    key={ref.id || ref._id}
                                                                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#eff4ff] dark:bg-[#1a2538] text-[#0070F3] border border-[#0070F3]/30 rounded-lg text-xs font-semibold shadow-2xs"
                                                                >
                                                                    <span className="material-symbols-outlined text-sm">folder_shared</span>
                                                                    <span className="truncate max-w-[160px]">{ref.name || ref.originalName}</span>
                                                                    <span className="text-[10px] opacity-70">(@File)</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCommentFileReferences((prev) => prev.filter((f) => (f.id || f._id) !== (ref.id || ref._id)))}
                                                                        className="hover:text-red-500 text-slate-400 ml-0.5 transition-colors cursor-pointer"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="comment-quill-footer flex items-center justify-between px-3 py-2 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/60 dark:bg-[#1a2234]/60">
                                                        <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate mr-2">
                                                            Press <kbd className="px-1 py-0.5 text-[10px] font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-2xs">Ctrl+Enter</kbd> to send · Type @ to mention people or files
                                                        </span>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {/* Reference project file (@File) button with Popover */}
                                                            <Popover
                                                                open={filePickerOpen}
                                                                onOpenChange={(open) => {
                                                                    setFilePickerOpen(open);
                                                                    if (open) loadProjectFiles();
                                                                }}
                                                                trigger="click"
                                                                placement="topLeft"
                                                                content={
                                                                    <div className="w-72 max-h-64 overflow-y-auto p-1 space-y-1">
                                                                        <div className="px-2 py-1 text-[11px] font-bold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider border-b border-slate-100 dark:border-[#303030]">
                                                                            Reference Project File (@File)
                                                                        </div>
                                                                        {loadingProjectFiles ? (
                                                                            <div className="py-4 text-center text-xs text-slate-400">Loading project files...</div>
                                                                        ) : projectFiles.length === 0 ? (
                                                                            <div className="py-4 text-center text-xs text-slate-400">No project files found</div>
                                                                        ) : (
                                                                            projectFiles.map((file) => (
                                                                                <div
                                                                                    key={file.id}
                                                                                    onClick={() => handleSelectFileReference(file)}
                                                                                    className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-xs transition-colors"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-sm text-[#0070F3]">
                                                                                        {file.icon || 'description'}
                                                                                    </span>
                                                                                    <div className="truncate flex-1">
                                                                                        <p className="font-semibold truncate text-[#0b1c30] dark:text-[#eaf1ff]">{file.name}</p>
                                                                                        <p className="text-[10px] text-slate-400 truncate">
                                                                                            {file.revisionRound || file.subfolderCategory || file.folderCode || 'File'} • {file.size}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )}
                                                                    </div>
                                                                }
                                                            >
                                                                <Tooltip title="Reference Project File (@File)">
                                                                    <button
                                                                        type="button"
                                                                        disabled={submittingComment || !targetProjectId}
                                                                        className="text-slate-400 dark:text-slate-300 hover:text-[#0070F3] dark:hover:text-[#38bdf8] transition-colors disabled:opacity-40 cursor-pointer p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">folder_shared</span>
                                                                    </button>
                                                                </Tooltip>
                                                            </Popover>

                                                            {/* Attach file button */}
                                                            <Tooltip title="Attach file">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => commentInputRef.current?.click()}
                                                                    disabled={submittingComment}
                                                                    className="text-slate-400 dark:text-slate-300 hover:text-primary dark:hover:text-[#38bdf8] transition-colors disabled:opacity-40 cursor-pointer p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                                                                >
                                                                    <span className="material-symbols-outlined text-[18px]">attach_file</span>
                                                                </button>
                                                            </Tooltip>

                                                            {/* Send button */}
                                                            <button
                                                                type="button"
                                                                onClick={handleAddComment}
                                                                disabled={submittingComment || (isQuillEmpty(commentText) && commentAttachments.length === 0 && (commentFileReferences?.length || 0) === 0)}
                                                                className="px-3 py-1.5 text-xs font-semibold text-white bg-[#03488A] hover:bg-[#003366] dark:bg-[#00AAF1] dark:hover:bg-[#0088cc] rounded-lg shadow-2xs transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                                            >
                                                                {submittingComment ? (
                                                                    <>
                                                                        <LoadingOutlined style={{ fontSize: 13 }} />
                                                                        <span>Sending…</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span>Send</span>
                                                                        <span className="material-symbols-outlined text-[15px] font-bold">send</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Upload size / type error (comment context) */}
                                        {uploadSizeError && (
                                            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl text-xs text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 duration-200 ml-11">
                                                <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5">error</span>
                                                <span>{uploadSizeError}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Hidden file input for comment attachments */}
                                    <input
                                        ref={commentInputRef}
                                        type="file"
                                        accept={ATTACHMENT_ACCEPT}
                                        className="hidden"
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files || []);
                                            if (files.length > 0) {
                                                handleInitiateUpload(files, 'comment');
                                            }
                                            e.target.value = '';
                                        }}
                                    />
                                </div>
                            );
                        })()}

                        {/* ── Attachments Tab ────────────────────────────────────────── */}
                        {activeTab === 'attachments' && (
                            <div className="space-y-4">
                                {/* Existing saved attachments */}
                                {task.attachments?.length > 0 ? (
                                    <div className="space-y-2.5">
                                        {/* Selection & Bulk Actions Bar */}
                                        <div className="flex items-center justify-between pb-1.5 border-b border-surface-container-high/60 text-xs">
                                            <label className="inline-flex items-center gap-2 cursor-pointer select-none text-ed-on-surface hover:text-[#03488A] transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={task.attachments.length > 0 && selectedAttachmentIds.length === task.attachments.length}
                                                    ref={(input) => {
                                                        if (input) {
                                                            input.indeterminate = selectedAttachmentIds.length > 0 && selectedAttachmentIds.length < task.attachments.length;
                                                        }
                                                    }}
                                                    onChange={handleToggleSelectAllAttachments}
                                                    className="w-4 h-4 rounded text-[#03488A] focus:ring-[#03488A] cursor-pointer accent-[#03488A]"
                                                />
                                                <span className="font-medium text-xs">
                                                    {selectedAttachmentIds.length > 0
                                                        ? `${selectedAttachmentIds.length} of ${task.attachments.length} selected`
                                                        : `Select all (${task.attachments.length})`}
                                                </span>
                                            </label>

                                            {selectedAttachmentIds.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedAttachmentIds([])}
                                                        className="text-xs text-ed-on-surface-variant hover:text-ed-on-surface hover:underline cursor-pointer"
                                                    >
                                                        Deselect
                                                    </button>
                                                    <Popconfirm
                                                        title={`Delete ${selectedAttachmentIds.length} selected attachment${selectedAttachmentIds.length > 1 ? 's' : ''}?`}
                                                        description="This will permanently delete the files from storage."
                                                        onConfirm={handleBulkDelete}
                                                        okText="Delete All"
                                                        okButtonProps={{ danger: true, size: 'small', loading: isBulkDeleting }}
                                                        cancelText="Cancel"
                                                        placement="bottomRight"
                                                    >
                                                        <button
                                                            type="button"
                                                            disabled={isBulkDeleting}
                                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/40 dark:hover:bg-red-900/60 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50"
                                                        >
                                                            {isBulkDeleting ? (
                                                                <LoadingOutlined style={{ fontSize: 13 }} />
                                                            ) : (
                                                                <DeleteOutlined style={{ fontSize: 13 }} />
                                                            )}
                                                            <span>Delete Selected ({selectedAttachmentIds.length})</span>
                                                        </button>
                                                    </Popconfirm>
                                                </div>
                                            )}
                                        </div>

                                        {/* Files Grid */}
                                        <div className="flex flex-wrap gap-2 max-h-[220px] overflow-y-auto pr-1">
                                            {task.attachments.map((attachment) => (
                                                <AttachmentItem
                                                    key={attachment._id}
                                                    attachment={attachment}
                                                    taskId={taskId}
                                                    onDelete={handleDeleteAttachment}
                                                    selectable={true}
                                                    selected={selectedAttachmentIds.map(String).includes(String(attachment._id))}
                                                    onToggleSelect={handleToggleSelectAttachment}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    pendingAttachments.length === 0 && (
                                        <div className="text-center py-4">
                                            <p className="text-xs text-on-surface-variant opacity-60">No files attached yet.</p>
                                        </div>
                                    )
                                )}

                                {/* Pending uploads list */}
                                {pendingAttachments.length > 0 && (
                                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                        {pendingAttachments.map((entry) => (
                                            <PendingFileChip
                                                key={entry.id}
                                                entry={entry}
                                                onRemove={removePendingAttachment}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Drop zone */}
                                <div
                                    onClick={() => attachTabInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setAttachmentsDragOver(true); }}
                                    onDragLeave={() => setAttachmentsDragOver(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setAttachmentsDragOver(false);
                                        handleInitiateUpload(e.dataTransfer.files, 'attachment');
                                    }}
                                    className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all
                                ${attachmentsDragOver
                                            ? 'border-primary bg-primary/5 dark:bg-blue-900/20'
                                            : 'border-surface-container-high dark:border-slate-800 hover:border-primary/40 dark:hover:border-blue-500/50 hover:bg-surface-container-low dark:hover:bg-slate-900/40'
                                        }`}
                                >
                                    <CloudUploadOutlined
                                        style={{ fontSize: 28, color: attachmentsDragOver ? '#03488A' : '#737782' }}
                                    />
                                    <div className="text-center">
                                        <p className="text-sm font-semibold text-on-surface dark:text-slate-100">
                                            Click to upload or drag & drop
                                        </p>
                                        <p className="text-xs text-on-surface-variant dark:text-slate-400 mt-0.5">
                                            Documents, images, archives, media · Max {MAX_ATTACHMENT_SIZE_MB}MB each
                                        </p>
                                    </div>
                                </div>

                                {/* Upload size / type error banner */}
                                {uploadSizeError && (
                                    <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl text-xs text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-1 duration-200">
                                        <span className="material-symbols-outlined text-base flex-shrink-0 mt-0.5">error</span>
                                        <span>{uploadSizeError}</span>
                                    </div>
                                )}

                                {/* Hidden input */}
                                <input
                                    ref={attachTabInputRef}
                                    type="file"
                                    accept={ATTACHMENT_ACCEPT}
                                    className="hidden"
                                    onChange={(e) => { handleInitiateUpload(e.target.files, 'attachment'); e.target.value = ''; }}
                                />

                                {/* Upload button — only shows when there are files queued */}
                                {pendingAttachments.length > 0 && (
                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleUploadPendingAttachments}
                                            disabled={!hasPendingToUpload}
                                            className="flex items-center gap-2 px-5 py-2 brand-gradient text-on-primary text-xs font-bold rounded-xl shadow-md hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
                                        >
                                            <CloudUploadOutlined />
                                            Upload {pendingAttachments.filter((f) => f.status === 'pending' || f.status === 'error').length} file(s)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* AI File Organization Modal (Section 21 Spec) */}
            <AiFileOrganizationModal
                open={aiOrgModalOpen}
                file={aiOrgFile}
                task={task}
                aiData={aiOrgData}
                onAccept={handleAcceptAiOrganization}
                onChooseExistingFile={handleChooseExistingFileFromAi}
                onCancel={() => {
                    setAiOrgModalOpen(false);
                    setAiOrgFile(null);
                    setAiOrgData(null);
                }}
            />

            {/* Pre-Upload Description & Reviewer Tagging Modal */}
            <PreUploadModal
                open={preUploadOpen}
                file={preUploadFile}
                employees={employees || []}
                onConfirm={handlePreUploadConfirm}
                onCancel={() => {
                    setPreUploadOpen(false);
                    setPreUploadFile(null);
                }}
            />
        </>
    );
}

/** Pick icon name + background colour for file type */
function getFileIconInfo(name, mime = '') {
    const filename = (name || '').toLowerCase();
    mime = mime.toLowerCase();
    if (mime.includes('pdf') || filename.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bgClass: 'bg-red-50 dark:bg-red-950/80 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-900/60' };
    if (mime.startsWith('image/') || filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif') || filename.endsWith('.svg') || filename.endsWith('.webp'))
        return { icon: 'image', bgClass: 'bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 border border-blue-200/80 dark:border-blue-900/60' };
    if (mime.includes('word') || filename.endsWith('.doc') || filename.endsWith('.docx') || filename.endsWith('.odt'))
        return { icon: 'description', bgClass: 'bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-900/60' };
    if (mime.includes('sheet') || filename.endsWith('.xls') || filename.endsWith('.xlsx') || filename.endsWith('.csv'))
        return { icon: 'table_chart', bgClass: 'bg-emerald-50 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-900/60' };
    if (mime.includes('zip') || mime.includes('rar') || filename.endsWith('.zip') || filename.endsWith('.rar') || filename.endsWith('.7z') || filename.endsWith('.tar') || filename.endsWith('.gz'))
        return { icon: 'folder_zip', bgClass: 'bg-amber-50 dark:bg-amber-950/80 text-amber-600 dark:text-amber-400 border border-amber-200/80 dark:border-amber-900/60' };
    if (mime.includes('video/') || filename.endsWith('.mp4') || filename.endsWith('.mov') || filename.endsWith('.avi') || filename.endsWith('.mkv') || filename.endsWith('.webm'))
        return { icon: 'videocam', bgClass: 'bg-purple-50 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400 border border-purple-200/80 dark:border-purple-900/60' };
    if (mime.includes('audio/') || filename.endsWith('.mp3') || filename.endsWith('.wav') || filename.endsWith('.m4a') || filename.endsWith('.ogg') || filename.endsWith('.flac'))
        return { icon: 'audio_file', bgClass: 'bg-pink-50 dark:bg-pink-950/80 text-pink-600 dark:text-pink-400 border border-pink-200/80 dark:border-pink-900/60' };
    return { icon: 'attach_file', bgClass: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200/80 dark:border-slate-700/60' };
}

/** Compact chip showing a pending / uploading / done / error file */
function PendingFileChip({ entry, onRemove }) {
    const { icon, bgClass } = getFileIconInfo(entry.name, entry.type);

    return (
        <div className="flex items-center gap-2 bg-surface-container-low dark:bg-[#1a2538] border border-surface-container-high dark:border-slate-700/80 rounded-xl px-2 py-1.5 min-w-[200px] max-w-[300px] flex-1">
            {entry.previewUrl ? (
                <img src={entry.previewUrl} alt={entry.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
            ) : (
                <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${bgClass}`}>
                    <span className="material-symbols-outlined text-[16px]">{icon}</span>
                </div>
            )}

            <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-on-surface dark:text-slate-100 truncate">{entry.name}</p>
                <p className="text-[11px] text-on-surface-variant dark:text-slate-400">
                    {formatBytes(entry.size)}
                    {entry.status === 'uploading' && ` · ${entry.progress || 0}%`}
                    {entry.error && <span className="text-error"> · {entry.error}</span>}
                </p>
                {entry.status === 'uploading' && (
                    <div className="h-1 mt-1 bg-surface-container-high dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#03488A] to-[#006591] transition-all"
                            style={{ width: `${entry.progress || 0}%` }}
                        />
                    </div>
                )}
            </div>

            <div className="flex-shrink-0 flex items-center gap-1.5">
                {entry.status === 'uploading' && (
                    <>
                        <LoadingOutlined style={{ fontSize: 13 }} className="text-primary dark:text-[#a7c8ff]" />
                        <Tooltip title="Cancel upload">
                            <button
                                type="button"
                                onClick={() => onRemove(entry.id)}
                                className="p-0.5 hover:text-red-500 text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors cursor-pointer flex items-center justify-center"
                            >
                                <CloseOutlined style={{ fontSize: 11 }} />
                            </button>
                        </Tooltip>
                    </>
                )}
                {entry.status === 'done' && <CheckCircleOutlined style={{ fontSize: 13 }} className="text-success dark:text-emerald-400" />}
                {(entry.status === 'pending' || entry.status === 'error') && (
                    <Tooltip title="Remove file">
                        <button
                            type="button"
                            onClick={() => onRemove(entry.id)}
                            className="p-0.5 hover:text-error text-on-surface-variant dark:text-slate-400 dark:hover:text-red-400 transition-colors cursor-pointer flex items-center justify-center"
                        >
                            <DeleteOutlined style={{ fontSize: 13 }} />
                        </button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}

function TabButton({ id, activeTab, setActiveTab, children }) {
    return (
        <button
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-all ${activeTab === id
                ? 'border-primary text-primary dark:text-[#a7c8ff] dark:border-[#a7c8ff]'
                : 'border-transparent text-on-surface-variant dark:text-slate-400 hover:text-primary dark:hover:text-[#a7c8ff]'
                }`}
        >
            {children}
        </button>
    );
}
